import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { typographyStyles } from '../styles/typography';
import { createT } from './simpleTranslate';
import { getDateFromStringOrDate } from '../../../utils/bill';
import { differenceDatesToString } from '../../../utils/index.jsx';
import { isInteger } from 'lodash';

/**
 * PDF SmartField Renderer
 * 
 * Renders complex smartfield types (treatment_prescription, allergy, background, etc.)
 * as formatted tables in PDF documents.
 */

interface PDFSmartFieldRendererProps {
  type: string;
  value: any; // Array of smartfield objects
  locale?: string;
}

const DATE_FIELDS_FORMAT: { [key: string]: string } = {
  "background-date": "YYYY",
  "treatment-start": "regular",
  "treatment-finish": "regular",
  "edd": "regular",
  "edd_last_period": "regular"
};

const TRANSLATED_COLUMNS = ["treatment-posology", "treatment-frecuency"];

export const PDFSmartFieldRenderer: React.FC<PDFSmartFieldRendererProps> = ({
  type,
  value,
  locale = 'fr',
}) => {
  const t = createT(locale);

  // Ensure value is an array
  const items = Array.isArray(value) ? value : [value];
  
  if (items.length === 0) {
    return (
      <Text style={typographyStyles.body2}>
        {t('general.no', 'Non')}
      </Text>
    );
  }

  // Special handling for treatment_prescription
  if (type === 'treatment_prescription') {
    return renderTreatmentPrescriptionTable(items, t, locale);
  }

  // Generic smartfield rendering (for other types like allergy, background, etc.)
  return renderGenericSmartFieldTable(items, type, t, locale);
};

/**
 * Render treatment_prescription as a table
 */
function renderTreatmentPrescriptionTable(
  items: any[],
  t: (key: string, fallback?: string) => string,
  locale: string
) {
  // Table styles
  const tableStyles = {
    table: {
      width: '100%',
      marginTop: 5,
      marginBottom: 5,
      borderWidth: 1,
      borderColor: '#000',
    },
    headerRow: {
      flexDirection: 'row' as const,
      backgroundColor: '#f0f0f0',
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 4,
    },
    row: {
      flexDirection: 'row' as const,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E0E0E0',
      paddingVertical: 3,
    },
    headerCell: {
      ...typographyStyles.body2,
      fontWeight: 'bold' as const,
      paddingHorizontal: 5,
    },
    cell: {
      ...typographyStyles.body2,
      paddingHorizontal: 5,
    },
    // Column widths for treatment_prescription
    drugCell: { width: '35%' },
    doseCell: { width: '20%' },
    posologyCell: { width: '20%' },
    durationCell: { width: '25%' },
    // SHER-464: meal instruction shown as a secondary line under the drug name.
    drugNameCell: { width: '35%', paddingHorizontal: 5 },
    mealLine: {
      ...typographyStyles.caption,
      color: '#555',
      fontStyle: 'italic' as const,
    },
  };

  // Process items similar to SmartField component
  const rows = items.map((element, index) => {
    let valueDict: any = { ...element };

    // Calculate duration for treatment_prescription
    if (element["treatment-finish"] !== null && element["treatment-finish"] !== undefined) {
      try {
        const startDate = getDateFromStringOrDate(element["treatment-start"]);
        const finishDate = getDateFromStringOrDate(element["treatment-finish"]);
        const objectDifference = differenceDatesToString(startDate, finishDate);
        const timeUnit = Object.keys(objectDifference)[0] as 'days' | 'weeks' | 'months';
        const durationValue = objectDifference[timeUnit];
        valueDict["duration"] = `${durationValue} ${t(`hospital.time-unit-options.${timeUnit}`, timeUnit)}`;
      } catch (error) {
        console.error('Error calculating duration:', error);
        valueDict["duration"] = '-';
      }
    } else {
      valueDict["duration"] = t('hospital.chronic', 'chronique / à long terme');
    }

    // Format frequency if it exists
    if (element["treatment-frecuency"]) {
      const frequencyKey = `hospital.frecuency-options.${element["treatment-frecuency"]}`;
      const translation = t(frequencyKey, element["treatment-frecuency"]);
      valueDict["treatment-frecuency"] = translation.includes('Missing translationId:')
        ? element["treatment-frecuency"]
        : translation;
    }

    // SHER-464: meal-relative intake instruction, shown as a secondary line under
    // the drug name. "other" uses the free text; the rest translate their key.
    const meal = element["treatment-meal-instruction"];
    if (meal) {
      if (meal === 'other') {
        valueDict["mealInstructionLabel"] = element["treatment-meal-instruction-other"] || '';
      } else {
        const mealTranslation = t(`hospital.meal-options.${meal}`, meal);
        valueDict["mealInstructionLabel"] = mealTranslation.includes('Missing translationId:') ? meal : mealTranslation;
      }
    } else {
      valueDict["mealInstructionLabel"] = '';
    }

    return valueDict;
  });

  return (
    <View style={tableStyles.table}>
      {/* Table Header */}
      <View style={tableStyles.headerRow}>
        <Text style={[tableStyles.headerCell, tableStyles.drugCell]}>
          {t('hospital.treatment_prescription-table', 'Médicament')}
        </Text>
        <Text style={[tableStyles.headerCell, tableStyles.doseCell]}>
          {t('hospital.treatment-dose-table', 'Quantité')}
        </Text>
        <Text style={[tableStyles.headerCell, tableStyles.posologyCell]}>
          {t('hospital.treatment-frecuency-table', 'Fréquence')}
        </Text>
        <Text style={[tableStyles.headerCell, tableStyles.durationCell]}>
          {t('hospital.treatment-duration', 'Durée')}
        </Text>
      </View>

      {/* Table Body */}
      {rows.map((row, index) => (
        <View key={index} style={tableStyles.row}>
          <View style={tableStyles.drugNameCell}>
            <Text style={typographyStyles.body2}>
              {row.treatment_prescription || '-'}
            </Text>
            {row.mealInstructionLabel ? (
              <Text style={tableStyles.mealLine}>{row.mealInstructionLabel}</Text>
            ) : null}
          </View>
          <Text style={[tableStyles.cell, tableStyles.doseCell]}>
            {row["treatment-dose"] || '-'}
          </Text>
          <Text style={[tableStyles.cell, tableStyles.posologyCell]}>
            {row["treatment-frecuency"] || '-'}
          </Text>
          <Text style={[tableStyles.cell, tableStyles.durationCell]}>
            {row.duration || '-'}
          </Text>
        </View>
      ))}
    </View>
  );
}

/**
 * Generic smartfield table renderer (for other types)
 */
function renderGenericSmartFieldTable(
  items: any[],
  type: string,
  t: (key: string, fallback?: string) => string,
  locale: string
) {
  if (items.length === 0) return null;

  // Get keys from first item (excluding code/id fields)
  const keys = Object.keys(items[0]).filter(
    key => !key.includes('code') && !key.includes('id')
  );

  if (keys.length === 0) {
    return (
      <Text style={typographyStyles.body2}>
        {t('general.no', 'Non')}
      </Text>
    );
  }

  // Table styles
  const tableStyles = {
    table: {
      width: '100%',
      marginTop: 5,
      marginBottom: 5,
      borderWidth: 1,
      borderColor: '#000',
    },
    headerRow: {
      flexDirection: 'row' as const,
      backgroundColor: '#f0f0f0',
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 4,
    },
    row: {
      flexDirection: 'row' as const,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E0E0E0',
      paddingVertical: 3,
    },
    headerCell: {
      ...typographyStyles.body2,
      fontWeight: 'bold' as const,
      paddingHorizontal: 5,
      width: `${100 / keys.length}%`,
    },
    cell: {
      ...typographyStyles.body2,
      paddingHorizontal: 5,
      width: `${100 / keys.length}%`,
    },
  };

  // Process items
  const rows = items.map((element) => {
    let valueDict: any = { ...element };
    
    for (const [key, val] of Object.entries(element)) {
      if (val && DATE_FIELDS_FORMAT.hasOwnProperty(key)) {
        const format = DATE_FIELDS_FORMAT[key];
        let dateObject = null;

        if (val && typeof (val as any).getMonth === 'function') {
          dateObject = val;
        } else if (val && Date.parse(val as string)) {
          dateObject = new Date((val as string).replace(' ', 'T').replace(' ', 'Z'));
        }

        if (dateObject && dateObject instanceof Date) {
          valueDict[key] = format === "regular" 
            ? dateObject.toLocaleDateString(locale) 
            : dateObject.getFullYear().toString();
        }
      } else if (val && TRANSLATED_COLUMNS.includes(key)) {
        const translation = t(`hospital.${key}-values.${val}`, val as string);
        valueDict[key] = translation.includes('Missing translationId:') ? val : translation;
      } else {
        if (typeof val === 'number') {
          valueDict[key] = isInteger(val) ? val.toString() : (val as number).toFixed(2);
        } else {
          valueDict[key] = val;
        }
      }
    }
    
    return valueDict;
  });

  return (
    <View style={tableStyles.table}>
      {/* Table Header */}
      <View style={tableStyles.headerRow}>
        {keys.map((key) => (
          <Text key={key} style={tableStyles.headerCell}>
            {t(`hospital.${key}-table`, key)}
          </Text>
        ))}
      </View>

      {/* Table Body */}
      {rows.map((row, index) => (
        <View key={index} style={tableStyles.row}>
          {keys.map((key) => (
            <Text key={key} style={tableStyles.cell}>
              {String(row[key] || '-')}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
}

export default PDFSmartFieldRenderer;

